// =============================================================================
// Home's attention strip, by reader (F16 UF-01).
//
// Each alert knows whether THIS reader can open the page its list lives on
// (`open`, from useModuleGate: true / false / null while the reads decide). An
// alert the reader can open shows as before. One they cannot open folds into a
// single line, "N more alerts, for other roles", that opens to plain text with
// no link — nothing is hidden for good, the count stays and so does the text.
// `null` (still deciding) never folds: the alert stays where it is, as text,
// so a slow read neither hides an alert nor jumps it later.
// =============================================================================
import { useState, type ReactNode } from 'react'
import { ChevronDown } from 'lucide-react'
import clsx from 'clsx'

export type AlertItem = {
  key: string
  node: ReactNode
  /** Can this reader open the alert's page? null = still deciding. */
  open: boolean | null
  /** False when the node renders nothing (e.g. a cooling pill with no batches):
   *  it is then not counted. Default true. */
  shows?: boolean
  /** The pill's tone; ranks the alert (F16 UF-02). Missing = ranked last. */
  tone?: AlertTone
}

export type AlertTone = 'red' | 'amber' | 'blue' | 'sky'

/** Worst first: red 0, amber 1, blue and sky 2 (no tone ranks with them). */
export function alertRank(tone: AlertTone | undefined): number {
  return tone === 'red' ? 0 : tone === 'amber' ? 1 : 2
}

/** Stable by rank: alerts of one rank keep the order they were pushed in. */
export function sortAlerts(items: AlertItem[]): AlertItem[] {
  return items
    .map((item, i) => ({ item, i }))
    .sort((a, b) => alertRank(a.item.tone) - alertRank(b.item.tone) || a.i - b.i)
    .map(x => x.item)
}

/** Split into the alerts kept in the strip and the ones folded away. */
export function splitAlerts(items: AlertItem[]): { kept: AlertItem[]; folded: AlertItem[] } {
  const shown = sortAlerts(items.filter(i => i.shows !== false))
  return {
    kept: shown.filter(i => i.open !== false),
    folded: shown.filter(i => i.open === false),
  }
}

export function foldedLabel(n: number): string {
  return `${n} more ${n === 1 ? 'alert' : 'alerts'}, for other roles`
}

export function AlertFold({ folded }: { folded: AlertItem[] }) {
  const [expanded, setExpanded] = useState(false)
  if (folded.length === 0) return null
  return (
    <div className="sm:col-span-2 min-w-0">
      <button
        type="button"
        onClick={() => setExpanded(v => !v)}
        aria-expanded={expanded}
        className="min-h-[44px] w-full rounded-xl border border-line px-3 py-2 text-left text-sm text-fg-muted flex items-center gap-2"
      >
        <ChevronDown size={16} className={clsx('shrink-0 transition-transform', expanded && 'rotate-180')} />
        <span>{foldedLabel(folded.length)}</span>
      </button>
      {expanded && (
        <div className="grid grid-cols-1 gap-2 mt-2 sm:grid-cols-2">
          {folded.map(f => <div key={f.key} className="min-w-0">{f.node}</div>)}
        </div>
      )}
    </div>
  )
}
